import React, { useState, useEffect } from 'react';
import { Crown, Trophy, Sparkles } from 'lucide-react';

export interface MarqueeRankAchievementItem {
  id: string;
  type: 'rank_milestone';
  userUid: string;
  username: string;
  church: string | null;
  country: string | null;
  rankName: string;
  division: string;
  achievedAt: string;
  text: string;
}

export interface MarqueeMonthlyTop10Item {
  id: string;
  type: 'monthly_top_10';
  monthLabel: string;
  monthKey: string;
  usernames: string[];
  text: string;
}

export type GlobalMarqueeAnnouncementItem =
  | MarqueeRankAchievementItem
  | MarqueeMonthlyTop10Item;

interface GlobalRankerMarqueeProps {
  refreshTrigger?: number;
}

export const GlobalRankerMarquee: React.FC<GlobalRankerMarqueeProps> = ({
  refreshTrigger = 0,
}) => {
  const [items, setItems] = useState<GlobalMarqueeAnnouncementItem[]>([]);
  const [monthLabel, setMonthLabel] = useState<string>(() =>
    new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(new Date())
  );

  useEffect(() => {
    let isMounted = true;

    const fetchMarquee = async () => {
      try {
        const res = await fetch('/api/rank/marquee');
        if (!res.ok) return;
        const data = await res.json();
        if (!isMounted) return;
        if (Array.isArray(data.items)) {
          setItems(data.items);
        }
        if (typeof data.monthLabel === 'string' && data.monthLabel) {
          setMonthLabel(data.monthLabel);
        }
      } catch {
        // Fail silently and keep existing items or hide gracefully
      }
    };

    fetchMarquee();

    // Refresh announcement data periodically (every 60s) independent of animation frames
    const interval = window.setInterval(fetchMarquee, 60000);
    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [refreshTrigger]);

  // Requirement 3.E: If neither source has content, hide the marquee gracefully
  if (!items || items.length === 0) {
    return null;
  }

  // Ensure the track has enough items for a wide seamless loop on large desktop screens
  const baseTrackItems: GlobalMarqueeAnnouncementItem[] =
    items.length === 1
      ? [
          items[0],
          { ...items[0], id: `${items[0].id}-dup-1` },
          { ...items[0], id: `${items[0].id}-dup-2` },
        ]
      : items.length === 2
      ? [
          items[0],
          items[1],
          { ...items[0], id: `${items[0].id}-dup-1` },
          { ...items[1], id: `${items[1].id}-dup-1` },
        ]
      : items;

  // Dynamic cycle speed (~48-65s) based on content volume for comfortable readability
  const cycleSeconds = Math.max(46, Math.min(68, baseTrackItems.length * 14));

  const renderTrackHalf = (suffix: string) => (
    <div
      className="flex items-center shrink-0"
      aria-hidden={suffix === 'clone' ? 'true' : undefined}
    >
      {baseTrackItems.map((item, idx) => {
        const isMonthly = item.type === 'monthly_top_10';
        return (
          <div
            key={`${item.id}-${suffix}-${idx}`}
            className="inline-flex items-center gap-2.5 sm:gap-3 px-4 sm:px-6 whitespace-nowrap"
          >
            {isMonthly ? (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-400 shrink-0">
                <Trophy className="w-3 h-3" />
              </span>
            ) : (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-[#F97316]/15 border border-[#F97316]/40 text-[#F97316] shrink-0">
                <Crown className="w-3 h-3" />
              </span>
            )}

            <span className="text-xs sm:text-[13px] font-medium text-[#E5E5E5] tracking-tight">
              {isMonthly ? (
                <>
                  <span className="font-bold text-amber-400">
                    Monthly Global Top 10 ({monthLabel})
                  </span>
                  <span className="mx-1.5 text-[#737373]">—</span>
                  <span>{item.text}</span>
                </>
              ) : (
                <>
                  <span className="font-bold text-[#F97316]">
                    Ranked Milestone
                  </span>
                  <span className="mx-1.5 text-[#737373]">—</span>
                  <span>{item.text}</span>
                </>
              )}
            </span>

            {/* Subtle visual separator between announcements */}
            <span
              aria-hidden="true"
              className="ml-3 sm:ml-5 inline-flex items-center text-amber-500/50 select-none"
            >
              <Sparkles className="w-3 h-3" />
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <section
      aria-label="Global Top Ranker Announcements"
      data-testid="global-ranker-marquee"
      className="relative w-full rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#111113] via-[#161412] to-[#111113] border border-[#27272A] overflow-hidden shadow-sm"
    >
      {/* Left & Right subtle edge fades for smooth visual entry/exit */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-6 sm:w-12 bg-gradient-to-r from-[#111113] to-transparent z-10"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-6 sm:w-12 bg-gradient-to-l from-[#111113] to-transparent z-10"
      />

      <div className="py-2 sm:py-2.5 overflow-hidden">
        <div
          data-testid="global-ranker-marquee-track"
          className="qb-marquee-track-ltr flex items-center w-max"
          style={{
            animationDuration: `${cycleSeconds}s`,
          }}
        >
          {renderTrackHalf('clone')}
          {renderTrackHalf('primary')}
        </div>
      </div>
    </section>
  );
};
